import { api } from '@/lib/api'
import { FORM_STATUS_MAP } from '../constants/statusAtendimento'

export interface SaveFormParams<T> {
  endpoint: string
  data: T
  atendimentoId: number
  pacienteId?: number
  empresaId?: number
  protocoloId?: number
  statusAtendimento?: string | null
  formName: string
}

export async function saveFormProtocolo<T extends Record<string, any>>({
  endpoint,
  data,
  atendimentoId,
  pacienteId,
  empresaId,
  protocoloId,
  statusAtendimento,
  formName
}: SaveFormParams<T>) {
  if (!atendimentoId) {
    throw new Error('ID do atendimento não fornecido')
  }

  if (!pacienteId || pacienteId <= 0) {
    throw new Error('ID do paciente não fornecido ou inválido')
  }

  // Remover campos null/undefined do data antes de mesclar
  const cleanData = Object.fromEntries(
    Object.entries(data).filter(([_, value]) => value !== null && value !== undefined)
  )

  const dataToSave: Record<string, any> = {
    id_atendimento: atendimentoId,
    id_paciente: pacienteId,
    ...cleanData
  }

  // Incluir id_empresa apenas se fornecido (o backend injeta automaticamente se não vier)
  if (empresaId) {
    dataToSave.id_empresa = empresaId
  }

  // Incluir status_atendimento apenas se for string válida (não null, não undefined, não vazio)
  if (statusAtendimento && typeof statusAtendimento === 'string' && statusAtendimento.trim()) {
    dataToSave.status_atendimento = statusAtendimento
  }

  let response
  console.log(`[saveFormProtocolo] Tentando salvar em ${endpoint}`, {
    atendimentoId,
    protocoloId,
    dataToSave
  })

  // Se já tem ID do protocolo, usar PUT diretamente
  if (protocoloId) {
    try {
      console.log(
        `[saveFormProtocolo] Protocolo ID fornecido (${protocoloId}), fazendo PUT diretamente`
      )
      response = await api.put(`${endpoint}/${protocoloId}`, dataToSave)
      console.log(
        `[saveFormProtocolo] PUT bem-sucedido para ${endpoint}/${protocoloId}`
      )
    } catch (error: any) {
      console.error(`[saveFormProtocolo] Erro ao atualizar protocolo:`, error)
      throw error
    }
  } else {
    // Se não tem ID, tentar POST primeiro (criar novo). Se já existir, fazer PUT (atualizar)
    try {
      // Tentar criar novo registro
      console.log(`[saveFormProtocolo] Fazendo POST para ${endpoint}`)
      response = await api.post(endpoint, dataToSave)
      console.log(`[saveFormProtocolo] POST bem-sucedido para ${endpoint}`)
    } catch (error: any) {
      console.log(
        `[saveFormProtocolo] POST falhou para ${endpoint}, status: ${error.response?.status}`,
        error
      )

      // Se der erro porque já existe, buscar o existente e atualizar
      if (
        error.response?.status === 400 ||
        error.response?.status === 409 ||
        error.response?.status === 422
      ) {
        try {
          console.log(
            `[saveFormProtocolo] Buscando protocolo existente em ${endpoint}`
          )
          const listResponse = await api.get(endpoint)
          const existingProtocolo = Array.isArray(listResponse.result)
            ? listResponse.result.find(
                (p: any) => p.id_atendimento === atendimentoId
              )
            : null

          if (existingProtocolo?.id) {
            console.log(
              `[saveFormProtocolo] Protocolo existente encontrado (ID: ${existingProtocolo.id}), fazendo PUT`
            )
            response = await api.put(
              `${endpoint}/${existingProtocolo.id}`,
              dataToSave
            )
            console.log(
              `[saveFormProtocolo] PUT bem-sucedido para ${endpoint}/${existingProtocolo.id}`
            )
          } else {
            // Se não encontrou, tenta POST novamente
            console.log(
              `[saveFormProtocolo] Protocolo não encontrado, tentando POST novamente`
            )
            response = await api.post(endpoint, dataToSave)
          }
        } catch (updateError: any) {
          console.error(
            `[saveFormProtocolo] Erro ao atualizar protocolo existente:`,
            updateError
          )
          // Se falhar ao buscar ou atualizar, re-lança o erro original
          throw error
        }
      } else {
        // Para outros erros, re-lança o erro
        console.error(`[saveFormProtocolo] Erro não tratado:`, error)
        throw error
      }
    }
  }

  // Atualizar status quando salvo
  const statusMap = FORM_STATUS_MAP[formName]
  // if (statusMap?.completion) {
  //   await updateAtendimentoStatus(atendimentoId, statusMap.completion)
  // }

  return response
}
